import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Grid, TextField } from '@mui/material';
import './SubAdminModal.css';
import MultipleSelect, {
  SelectOption,
} from '../../MasterComponent/Select/MultipleSelect';
import MultipleSearchSelect from '../../MasterComponent/Select/MultipleSearchSelect';
import { CustomModal } from '..';
import { useFetchData } from '../../../hooks/useFetchData';
import { config } from './config';
import Loading from '../../Loading/Loading';
import { useDispatch, useSelector } from 'react-redux';
import { organizationState } from '../../../store';
import { userActions } from '../../../store/userSlice';

interface SubAdminFormData {
  EmailAddress: string;
  Password: string;
  FirstName: string;
  LastName: string;
  ContactNo: string;
  Designation: string;
  assignedCompanies: string[];
  assignedFeatures: string[];
}

interface SubAdminModalProps {
  show: boolean;
  onClose: () => void;
}

export const SubAdminModal: React.FC<SubAdminModalProps> = ({
  show,
  onClose,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [featureOptions, setFeatureOptions] = useState<SelectOption[]>([]);
  const [submitError, setSubmitError] = useState<string>('');
  const dispatch = useDispatch();

  const { organizations, isLoading } = useSelector(organizationState);

  // Fetch features list
  const { data: featuresData, loader: featuresLoader } = useFetchData({
    fetchData: () => config.listAllFeatures(),
    fetchOnMount: true,
  });

  // Format features data when it's loaded
  useEffect(() => {
    if (featuresData && Array.isArray(featuresData)) {
      // Convert API response array to SelectOption[]
      const formattedFeatures: SelectOption[] = featuresData.map(
        (feature: any) => ({
          id: feature.id,
          label: feature.name,
          value: String(feature.id),
        })
      );
      setFeatureOptions(formattedFeatures);
    }
  }, [featuresData]);

  // Format organizations data from Redux
  const companyOptions: SelectOption[] = useMemo(() => {
    if (!organizations || !Array.isArray(organizations)) {
      return [];
    }

    return organizations.map((org: any, index: number) => ({
      id: index,
      label: org.name,
      value: org.name,
    }));
  }, [organizations]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SubAdminFormData>({
    defaultValues: {
      EmailAddress: '',
      Password: '',
      FirstName: '',
      LastName: '',
      ContactNo: '',
      Designation: '',
      assignedCompanies: [],
      assignedFeatures: [],
    },
  });

  const onSubmit = async (data: SubAdminFormData) => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const formattedData = {
        EmailAddress: data.EmailAddress,
        Password: data.Password,
        FirstName: data.FirstName,
        LastName: data.LastName,
        ContactNo: data.ContactNo,
        Designation: data.Designation,
        assignedCompanies: data.assignedCompanies,
        assignedFeatures: data.assignedFeatures.map(Number),
      };
      const result = await config.addSubAdminAccount(formattedData);

      setIsSubmitting(false);
      reset();
      dispatch(
        userActions.replaceUserState({
          successMessage: result.message,
        })
      );
      onClose();
    } catch (error: any) {
      console.error('Error creating sub admin:', error);
      setSubmitError(
        error?.error?.message || 'An error occurred while creating sub admin'
      );
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setSubmitError('');
      onClose();
    }
  };

  return (
    <CustomModal
      show={show}
      onClose={handleClose}
      headerMsg="Create Sub Support Admin"
      buttonMsg={isSubmitting ? 'Creating...' : 'Create Sub Admin'}
      submitFunc={handleSubmit(onSubmit)}
      isSubmitBtnDisabled={isSubmitting || featuresLoader || isLoading}
      style={{ maxWidth: '700px', width: '90%' }}
    >
      {isSubmitting ? (
        <div className="loadingContainer">
          <Loading style={{ minHeight: '53vh' }} />
        </div>
      ) : (
        <form className="subAdminForm">
          <Grid container spacing={3}>
            {/* Personal Information Section */}
            <Grid item xs={12}>
              <h3 className="sectionSubAdminTitle">Personal Information</h3>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="FirstName"
                control={control}
                rules={{
                  required: 'First name is required',
                  minLength: {
                    value: 2,
                    message: 'Minimum 2 characters required',
                  },
                }}
                render={({ value, onChange }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="First Name"
                    fullWidth
                    variant="outlined"
                    error={!!errors.FirstName}
                    helperText={errors.FirstName?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="LastName"
                control={control}
                rules={{
                  required: 'Last name is required',
                  minLength: {
                    value: 2,
                    message: 'Minimum 2 characters required',
                  },
                }}
                render={({ value, onChange }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Last Name"
                    fullWidth
                    variant="outlined"
                    error={!!errors.LastName}
                    helperText={errors.LastName?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="EmailAddress"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }}
                render={({ value, onChange }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Email Address"
                    type="email"
                    fullWidth
                    variant="outlined"
                    error={!!errors.EmailAddress}
                    helperText={errors.EmailAddress?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="ContactNo"
                control={control}
                rules={{
                  required: 'Contact number is required',
                  pattern: {
                    value: /^\+?[1-9]\d{1,14}$/,
                    message: 'Invalid contact number (e.g., +1234567890)',
                  },
                }}
                render={({ value, onChange }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Contact Number"
                    fullWidth
                    variant="outlined"
                    placeholder="+1234567890"
                    error={!!errors.ContactNo}
                    helperText={errors.ContactNo?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>

            {/* Account Information Section */}
            <Grid item xs={12}>
              <h3 className="sectionSubAdminTitle">Account Information</h3>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="Password"
                control={control}
                rules={{
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                    message:
                      'Password must contain uppercase, lowercase, number and special character',
                  },
                }}
                render={({ value, onChange }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    error={!!errors.Password}
                    helperText={errors.Password?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="Designation"
                control={control}
                rules={{
                  required: 'Designation is required',
                  minLength: {
                    value: 2,
                    message: 'Minimum 2 characters required',
                  },
                }}
                render={({ value, onChange }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Designation"
                    fullWidth
                    variant="outlined"
                    placeholder="e.g., Regional Admin"
                    error={!!errors.Designation}
                    helperText={errors.Designation?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>

            {/* Access & Permissions Section */}
            <Grid item xs={12}>
              <h3 className="sectionSubAdminTitle">Access & Permissions</h3>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="assignedCompanies"
                control={control}
                rules={{
                  validate: (value) =>
                    value.length > 0 || 'At least one company must be selected',
                }}
                render={({ value, onChange }) => (
                  <div>
                    {isLoading ? (
                      <div className="fetchingContainer">
                        <MultipleSearchSelect
                          options={[]}
                          selectedValues={[]}
                          // eslint-disable-next-line @typescript-eslint/no-empty-function
                          onChange={() => {}}
                          label="Assigned Companies"
                          placeholder="Loading organizations..."
                          searchPlaceholder="Search companies..."
                          maxVisibleChips={3}
                          // disabled={true}
                          highlightCheckedColor="#0078db"
                        />
                      </div>
                    ) : (
                      <MultipleSearchSelect
                        options={companyOptions}
                        selectedValues={value || []}
                        onChange={onChange}
                        label="Assigned Companies"
                        placeholder="Select companies to assign..."
                        searchPlaceholder="Search companies..."
                        maxVisibleChips={3}
                        // disabled={isSubmitting}
                        highlightCheckedColor="#0078db"
                      />
                    )}
                    {errors.assignedCompanies && (
                      <p className="errorText">
                        At least one company must be selected
                      </p>
                    )}
                  </div>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="assignedFeatures"
                control={control}
                rules={{
                  validate: (value) =>
                    value.length > 0 || 'At least one feature must be selected',
                }}
                render={({ value, onChange }) => (
                  <div>
                    {featuresLoader ? (
                      <div className="fetchingContainer">
                        <MultipleSelect
                          options={[]}
                          selectedValues={[]}
                          // eslint-disable-next-line @typescript-eslint/no-empty-function
                          onChange={() => {}}
                          label="Assigned Features"
                          placeholder="Fetching features..."
                          maxVisibleChips={3}
                          // disabled={true}
                          highlightCheckedColor="#0078db"
                        />
                      </div>
                    ) : (
                      <MultipleSelect
                        options={featureOptions}
                        selectedValues={value || []}
                        onChange={onChange}
                        label="Assigned Features"
                        placeholder="Select features to grant access..."
                        maxVisibleChips={3}
                        // disabled={isSubmitting}
                        highlightCheckedColor="#0078db"
                      />
                    )}
                    {errors.assignedFeatures && (
                      <p className="errorText">
                        At least one feature must be selected
                      </p>
                    )}
                  </div>
                )}
              />
            </Grid>
          </Grid>
          {submitError && (
            <div
              style={{
                color: '#d32f2f',
                backgroundColor: '#ffebee',
                padding: '12px 16px',
                borderRadius: '4px',
                marginTop: '16px',
                border: '1px solid #ef9a9a',
              }}
            >
              {submitError}
            </div>
          )}
        </form>
      )}
    </CustomModal>
  );
};
